import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';

// WP8 stage-1 MUST-DO (final-review.md, "Parked-item adjudications (a)"):
// there was NO error boundary anywhere in web/src. A failed lazy `import()`
// (e.g. a hash-renamed chunk 404ing right after a deploy, while a customer's
// tab still has the OLD index.html open) rejects the Suspense boundary and
// unmounts the whole tree — the entire SPA goes blank with no recovery path.
// This is a real, reproducible failure mode, worst at exactly the moment
// WP8 stage 1 deploys.
//
// Mounted in several places (main.tsx, panel-shell.tsx): around the route
// outlet inside PanelShell (nav survives — keyed by `location.pathname` so
// navigating away from a crashed route clears the error automatically:
// React only calls getDerivedStateFromError once per mount, a `key` change
// forces a fresh mount instead of relying on any reset-on-props trick);
// around each org-level lazy route that mounts its OWN PanelShell internally
// (Locations, SurveyAnalytics, SurveyBuilder — see RouteBoundary below, no
// nav to preserve there since the shell doesn't exist until the lazy import
// resolves, fix round 1); and once around the whole router as a last
// resort, in case the crash happens in the shell itself (PanelShell) before
// any inner boundary is reachable.
//
// Fix round 1 (2026-08-17, error-boundary-review.md): tightened from a bare
// `import` alternative, which matched as a substring in ANY message
// containing the word "import" (e.g. "...reading 'importantField'"),
// producing a false "new version released" claim on unrelated errors. The
// alternatives below are the actual browser-emitted phrasings for a failed
// dynamic import: Chrome/V8 ("Failed to fetch dynamically imported
// module: ..."), Firefox ("error loading dynamically imported module: ..."
// — both covered by "dynamically imported module"), Safari ("Importing a
// module script failed."), and Vite's own preload-error wrapper
// ("Failed to load chunk / loading chunk").
const STALE_CHUNK_PATTERN =
  /dynamically imported module|importing a module script failed|loading chunk/i;

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console -- deliberate: this is the last
    // line of defense once Sentry's own error capture may not have wired up
    // for whatever crashed; keep a visible trail in the browser console too.
    console.error('[ErrorBoundary] caught a render error', error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isStaleChunk = STALE_CHUNK_PATTERN.test(error.message);

    return (
      <div className="flex flex-col items-start gap-3 rounded-xl border border-border-subtle bg-card p-6">
        <p role="alert" className="text-sm text-danger-fg">
          {isStaleChunk
            ? 'a new version of pageboss was just released — reload to get it'
            : 'something went wrong loading this screen'}
        </p>
        <p className="text-sm text-text-secondary">
          {isStaleChunk
            ? "the page you're on refers to files that were replaced by the latest release."
            : "it's not you — this happens sometimes. reloading usually fixes it."}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={this.handleReload}>
          reload
        </Button>
      </div>
    );
  }
}

/**
 * Fix round 1: a handful of `/app/*` routes (Locations, SurveyAnalytics,
 * SurveyBuilder) are lazy-loaded as children of the bare `<App>` layout,
 * NOT of `BusinessLayout` — each self-mounts `PanelShell` internally, once
 * its own chunk finishes loading. That means the `<Suspense>` guarding
 * their `import()` sits OUTSIDE any shell/boundary, so a rejected import
 * (a stale-chunk 404 right after a deploy) was previously caught only by
 * the top-level, no-key, no-nav last-resort boundary in main.tsx — the
 * exact "whole SPA blanks" failure this task exists to prevent.
 *
 * `RouteBoundary` is the shared fix for that shape: wrap each such lazy
 * route's `<Suspense>` in this component instead of a bare `<ErrorBoundary>`.
 * Nav genuinely cannot survive here (the shell that would hold it hasn't
 * mounted yet when the import rejects) — that's an accepted trade-off, not
 * a bug. What matters is a keyed, resettable fallback with the reload
 * button, scoped to just this route, instead of the app-wide catch-all.
 * Keying by `location.pathname` gives it the same self-heal-on-navigation
 * behavior as the inner PanelShell boundary.
 */
export function RouteBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}
